// src/components/Control/Operator/Laser.tsx
// «Лазер и рулетка» (Настройки): лазерная рулетка TW10S на поворотной камере 1 (нода laser
// на Pi). Навести луч на точку стены перед роботом («120 см от пола, 2,5 м от левой стены»)
// и мерить расстояния.
//   robot/<id>/laser/cmd   <- {"cmd":"aim","height","offset","side","survey","by"} (метры; by: "along" —
//                             вдоль стены, "straight" — по прямой от боковой стены) |
//                             {"cmd":"approach","point":[x,y],"height","survey","by"} — клик по стене на схеме:
//                             подъехать и навести | {"cmd":"config","walls_sens"} | {"cmd":"stop"} |
//                             {"cmd":"laser","on"} | {"cmd":"measure"} | {"cmd":"forget"}
//   robot/<id>/laser/state -> LaserState (telemetry)
// Робот находит стену и её угол лидаром, обмеряет стену рулеткой (так находится
// поправка серво), наводит и проверяет расстояние рулеткой. Изогнутую стену рулетка
// проходит по высоте цели (её профиль), а наведение доводится по рулетке. Схема — сверху,
// в осях робота: точки лидара, найденные стены; робот внизу, «вперёд» — вверх.
//   robot/<id>/laser/spot  -> {"on","px":[u,v],...}: где кончается луч — LaserDot рисует его поверх
//                             камеры 1 (пока модуля нет — это и есть «лазер»)
import React, { useState } from 'react';
import { useRobot, useTelemetryState } from '../robot/RobotContext';
import { useZenohJson } from '../../../hooks/useZenohSubscription';
import { LaserSide, LaserState, STALE, fresh } from '../robot/telemetry';
import { putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { loadPref, savePref, timeOf } from '../../../types/features';
import { CameraView } from './Camera';
import { Segmented } from './ui';
import { Pt, WallsCard } from './LaserWalls';

const STATUS: Record<string, [string, string]> = {
  idle: ['ожидание', 'off'], walls: ['ищу стену и угол лидаром…', 'warn'], survey: ['обмеряю стену рулеткой…', 'warn'],
  aiming: ['навожу…', 'warn'], done: ['наведено', 'ok'], planning: ['выбираю, куда встать…', 'warn'],
  driving: ['еду к стене…', 'warn'], turning: ['поворачиваюсь к стене…', 'warn'], error: ['не получилось', 'alert'], stopped: ['остановлено', 'off'],
};
const SIDE_RU: Record<LaserSide, string> = { left: 'левой', right: 'правой' };
type By = 'along' | 'straight';
const PREF = 'laser.target';

const cm = (m: number) => Math.round(m * 1000) / 10;
const num = (v: number, digits = 1) => v.toLocaleString('ru-RU', { maximumFractionDigits: digits, minimumFractionDigits: digits });

function useLaser() {
  useTick(1000);
  const { session, robotId } = useRobot();
  const { st, at } = useTelemetryState(t => ({ st: t.laser, at: t.laserAt }));
  const send = (msg: object) => putJson(session, `robot/${robotId}/laser/cmd`, msg);
  return { st: fresh(at, STALE.node) ? st : null, at, send };
}

// ---------- навести ----------

const CmField: React.FC<{ label: string; value: string; onChange: (v: string) => void; min: number; max: number; id: string }> = ({ label, value, onChange, min, max, id }) => (
  <label className="op-num" htmlFor={id}>
    <span>{label}</span>
    <input id={id} type="number" inputMode="decimal" min={min} max={max} step={0.5} value={value} onChange={e => onChange(e.target.value)} />
  </label>
);

// Поля формы — общие для «Навести» и для клика по стене на схеме
interface Form { height: string; offset: string; side: LaserSide; survey: boolean; by: By }

function useForm(st: LaserState | null) {
  const [form, setFormState] = useState<Form>(() =>
    loadPref(PREF, { height: '120', offset: '250', side: 'left' as LaserSide, survey: true, by: 'along' as By }));
  const setForm = (patch: Partial<Form>) => setFormState(f => ({ ...f, ...patch }));
  const h = Number(form.height.replace(',', '.')), o = Number(form.offset.replace(',', '.'));
  const survey = form.survey && !st?.virtual;
  const blocked = !st ? 'Нода laser на роботе не отвечает (не запущена или старая версия ПО на Pi).'
    : st.asleep ? 'Робот спит.'
      : !st.pan_tilt ? 'Поворотная камера не отвечает (нода pan_tilt).'
        : survey && !st.ok ? 'Рулетка не на связи — без неё можно навести только по лидару (снимите «Обмерить»).' : null;
  const save = () => savePref(PREF, form);
  return { form, setForm, h, o, survey, blocked, save };
}
type FormApi = ReturnType<typeof useForm>;

const AimCard: React.FC<{ st: LaserState | null; send: (m: object) => void; f: FormApi }> = ({ st, send, f }) => {
  const { form, setForm, h, o, survey, blocked } = f;
  const { height, offset, side, by } = form;
  const setHeight = (v: string) => setForm({ height: v });
  const setOffset = (v: string) => setForm({ offset: v });
  const setSide = (v: LaserSide) => setForm({ side: v });
  const setBy = (v: By) => setForm({ by: v });
  const setSurvey = (v: boolean) => setForm({ survey: v });
  const valid = Number.isFinite(h) && Number.isFinite(o) && h >= 0 && h <= 350 && o >= 0 && o <= 3000;
  const res = st?.result;
  const [label, level] = STATUS[st?.status ?? 'idle'] ?? STATUS.idle;

  const aim = () => {
    f.save();
    send({ cmd: 'aim', height: h / 100, offset: o / 100, side, survey, by });
  };

  return (
    <div className="feature-card">
      <h3>
        <span>Навести на точку стены</span>
        {st && <span className={`badge ${level}`}>{label}</span>}
      </h3>
      <p className="hint">Стена — та, к которой робот обращён; точка задаётся от её угла с левой или правой стеной.
        Поставьте робота лицом к стене, угол должен быть виден лидару — или кликните по стене на схеме выше:
        робот подъедет сам (на высоту из этой формы).</p>
      <div className="op-num-grid">
        <CmField id="laser-h" label="Высота от пола, см" value={height} onChange={setHeight} min={0} max={350} />
        <CmField id="laser-o" label={`От ${SIDE_RU[side]} стены, см`} value={offset} onChange={setOffset} min={0} max={3000} />
      </div>
      <div className="row">
        <Segmented label="От какой стены" value={side} onChange={setSide}
                   options={[['left', 'от левой стены'], ['right', 'от правой стены']]} />
      </div>
      <div className="row">
        <Segmented<By> label="Как мерить расстояние" value={by} onChange={setBy}
                       options={[['along', 'вдоль стены'], ['straight', 'по прямой']]} />
      </div>
      <p className="hint">На плоской стене одно и то же. На изогнутой: «вдоль стены» — как рулетку прикладывают к
        поверхности от угла; «по прямой» — как меряют от боковой стены поперёк комнаты.</p>
      <label className="op-check">
        <input type="checkbox" checked={survey} disabled={!!st?.virtual} onChange={e => setSurvey(e.target.checked)} />
        Обмерить стену рулеткой — точнее, ~20 с: робот сам поправляет ошибки серво
      </label>
      {st?.virtual && <p className="hint">Рулетки пока нет — лазер виртуальный: наведение только по лидару, точка луча
        рисуется красным поверх камеры 1.</p>}
      <div className="row">
        <button type="button" className="op-btn primary" disabled={!valid || !!blocked || st?.busy} onClick={aim}>
          Навести
        </button>
        <button type="button" className="op-btn" disabled={!st || (!st.busy && !st.laser)} onClick={() => send({ cmd: 'stop' })}>
          Стоп и выключить луч
        </button>
      </div>
      {blocked && <p className="hint">{blocked}</p>}
      {st?.busy && st.progress && (
        <div className="op-laser-progress" role="progressbar" aria-valuemin={0} aria-valuemax={st.progress[1]} aria-valuenow={st.progress[0]}
             aria-label="Обмер стены">
          <i style={{ width: `${(100 * st.progress[0]) / Math.max(1, st.progress[1])}%` }} />
          <span>замер {st.progress[0] + 1} из {st.progress[1]}</span>
        </div>
      )}
      {st?.reason && !st.busy && <p className={st.status === 'error' ? 'error-text' : 'hint'}>{st.reason}</p>}
      {st?.moved && <p className="error-text">{st.moved}</p>}
      {res && !st?.busy && <AimResult res={res} />}
    </div>
  );
};

const AimResult: React.FC<{ res: NonNullable<LaserState['result']> }> = ({ res }) => {
  const c = res.check;
  const far = c?.diff_mm != null && Math.abs(c.diff_mm) > 30;
  const byText = res.wall?.kind === 'curved' ? (res.target.by === 'straight' ? ' (по прямой)' : ' (вдоль стены)') : '';
  return (
    <ul className="op-laser-facts">
      {(res.warnings ?? []).map((w, i) => <li key={i} className="warn">{w}</li>)}
      <li><b>Цель:</b> {num(cm(res.target.height))} см от пола, {num(cm(res.target.offset))} см от {SIDE_RU[res.target.side]} стены{byText},
        до неё {num(res.distance, 2)} м; камера: {num(Math.abs(res.pan))}° {res.pan >= 0 ? 'влево' : 'вправо'},
        {' '}{num(Math.abs(res.tilt))}° {res.tilt >= 0 ? 'вверх' : 'вниз'}.</li>
      {res.error_cm != null
        ? <li><b>Ожидаемая точность:</b> около ±{num(res.error_cm)} см — это в основном шаг серво ({num(res.step_cm)} см на таком
          расстоянии).</li>
        : <li><b>Без обмера:</b> точность — несколько сантиметров и больше, смотря по калибровке серво.</li>}
      {res.approach && (
        <li><b>Подъезд:</b> встал в {num(res.approach.dist, 2)} м от стены напротив точки (она в {num(cm(res.approach.offset))} см
          от {res.approach.side === 'left' ? 'левого' : 'правого'} угла); всего {num(res.approach.seconds, 0)} с.</li>
      )}
      {res.wall?.kind === 'curved' && (
        <li><b>Стена изогнута</b> (по лидару ~{num(Math.abs(res.wall.sag_cm), 0)} см от прямой): {res.survey
          ? 'рулетка прошла по ней на высоте цели — точка взята по этому профилю'
          : 'точка взята по профилю лидара'}{res.loop ? `; наведение доведено по рулетке за ${res.loop.tries} ${res.loop.tries === 1 ? 'замер' : 'замера'}, остаток ${res.loop.residual_mm} мм` : ''}.</li>
      )}
      {res.survey && (
        <li><b>Обмер:</b> {res.survey.used} замеров{res.survey.dropped ? ` (отброшено ${res.survey.dropped} — предметы на стене, угол)` : ''},
          разброс {res.survey.rms_mm ?? '—'} мм; боковая стена {res.survey.side ? 'обмерена рулеткой' : 'взята по лидару'};
          цель сдвинулась против расчёта по лидару на {num(res.shift_cm)} см. {num(res.seconds, 0)} с.
          {res.survey.saved === false ? ' Калибровка серво не сохранена.' : ''}</li>
      )}
      {c && (
        <li className={far ? 'warn' : ''}><b>Проверка рулеткой:</b>{' '}
          {c.measured == null ? 'нет отражения' : `${num(c.measured, 3)} м, по расчёту ${num(c.expected, 3)} м (${c.diff_mm! > 0 ? '+' : ''}${c.diff_mm} мм)`}
          {far ? ' — на пути луча что-то есть или стена не там, где её видит лидар.' : ''}</li>
      )}
    </ul>
  );
};

// ---------- рулетка, калибровка ----------

const MeterCard: React.FC<{ st: LaserState | null; send: (m: object) => void }> = ({ st, send }) => {
  const m = st?.measure;
  return (
    <div className="feature-card">
      <h3>
        <span>Рулетка</span>
        {!st ? <span className="badge off">нет данных</span>
          : st.ok ? <span className="badge ok">{st.virtual ? (st.laser ? 'виртуальная · луч на камере' : 'виртуальная') : st.laser ? 'луч горит' : 'на связи'}</span>
            : <span className="badge alert">не на связи</span>}
      </h3>
      {st && !st.ok && <p className="error-text">{st.error ?? 'Рулетка не отвечает'}</p>}
      {st?.ok && <p className="hint">{st.virtual ? st.device : `${st.device} · ${st.port}`}</p>}
      <div className="row">
        <button type="button" className={`op-btn ${st?.laser ? 'active' : ''}`} disabled={!st?.ok || st.busy}
                aria-pressed={!!st?.laser} onClick={() => send({ cmd: 'laser', on: !st?.laser })}>
          {st?.laser ? 'Выключить луч' : 'Включить луч'}
        </button>
        <button type="button" className="op-btn" disabled={!st?.ok || st.busy} onClick={() => send({ cmd: 'measure' })}>
          Измерить
        </button>
      </div>
      {m && (
        <p className="op-laser-reading" aria-live="polite">
          {m.distance == null ? 'Нет отражения' : <><b>{num(m.distance, 3)} м</b> <span className="hint">· {timeOf(m.at)}</span></>}
          {m.on_wall && (
            <span className="hint"><br />На стене: {num(cm(m.on_wall.height))} см от пола, {num(cm(m.on_wall.offset))} см
              от {SIDE_RU[m.on_wall.side]} стены{m.on_wall.by === 'straight' ? ' (по прямой)' : ''}
              {Math.abs(m.on_wall.off_wall) > 0.02 ? ` — но в ${num(cm(m.on_wall.off_wall), 0)} см от плоскости стены: луч попал на предмет` : ''}.</span>
          )}
        </p>
      )}
      <p className="hint">Луч светит туда, куда смотрит камера 1: поворачивайте её кнопками поверх кадра. «На стене» —
        по стене последнего наведения. Луч лазера класса 2: не направляйте в глаза; гаснет сам через 10 минут.</p>
    </div>
  );
};

const CalibrationCard: React.FC<{ st: LaserState | null; send: (m: object) => void }> = ({ st, send }) => {
  if (!st) return null;
  const c = st.cal;
  const empty = !st.calibrated_at;
  const forget = () => {
    if (window.confirm('Сбросить калибровку серво? Следующее наведение без обмера будет грубым.')) send({ cmd: 'forget' });
  };
  return (
    <div className="feature-card">
      <h3><span>Калибровка серво</span><span className={`badge ${empty ? 'off' : 'ok'}`}>{empty ? 'нет' : 'есть'}</span></h3>
      {empty ? <p className="hint">Появится после первого наведения с обмером.</p> : (
        <ul className="op-laser-facts">
          <li>Поворот: смещение {num(c.pan_b, 2)}°, масштаб {c.pan_k >= 0 ? '+' : ''}{num(c.pan_k * 100)}%</li>
          <li>Наклон: смещение {num(c.tilt_b, 2)}°, масштаб {c.tilt_k >= 0 ? '+' : ''}{num(c.tilt_k * 100)}%</li>
          <li>Найдена: {new Date(st.calibrated_at!).toLocaleString('ru-RU')}</li>
        </ul>
      )}
      <div className="row">
        <button type="button" className="op-btn" disabled={empty || st.busy} onClick={forget}>Сбросить калибровку</button>
      </div>
      <p className="hint">Каждый обмер находит её заново (серво уходят со временем); без обмера наведение берёт последнюю.
        Положение рулетки на кронштейне — в «Геометрии робота». Точнее шаг серво — частота ШИМ 100 Гц
        (<code>pwm_hz</code> ноды pan_tilt), если серво её держат.</p>
    </div>
  );
};

// Точка луча поверх кадра камеры 1 (laser/spot). Камера поворачивается вместе с лазером,
// поэтому точка почти всегда у середины кадра — смещена только параллаксом.
export const LaserDot: React.FC = () => {
  useTick(1000);
  const { session, robotId } = useRobot();
  const [spot, setSpot] = useState<{ on: boolean; px?: number[] | null; distance?: number | null; at: number } | null>(null);
  useZenohJson<{ on: boolean; px?: number[] | null; distance?: number | null }>(
    session, `robot/${robotId}/laser/spot`, m => setSpot({ ...m, at: Date.now() }));
  if (!spot?.on || !spot.px || Date.now() - spot.at > STALE.node) return null;
  const [u, v] = spot.px;
  if (u < 0 || u > 1 || v < 0 || v > 1) return null;
  return (
    <div className="op-laser-layer" aria-hidden="true">
      <span className="op-laser-dot" style={{ left: `${u * 100}%`, top: `${v * 100}%` }}
            title={spot.distance != null ? `Лазер: ${num(spot.distance, 2)} м` : 'Лазер'} />
    </div>
  );
};

// Почему на схеме пусто
function useNoScanWhy(): string {
  const status = useTelemetryState(t => t.status);
  return status?.link === 'offline' ? 'контроллер шасси (ESP32) не на связи — включите робота или проверьте его Wi-Fi'
    : status && status.lidar === false ? 'лидар не присылает сканы'
      : 'сканов лидара пока нет';
}

const LaserPanel: React.FC = () => {
  const { st, at, send } = useLaser();
  const f = useForm(st);
  const why = useNoScanWhy();
  const res = st?.result;
  const approach = (p: Pt) => {
    f.save();
    send({ cmd: 'approach', point: [Math.round(p[0] * 1000) / 1000, Math.round(p[1] * 1000) / 1000], height: f.h / 100,
           survey: f.survey, by: f.form.by });
  };
  return (
    <div className="op-laser-page">
      <WallsCard st={st} at={at} height={(Number.isFinite(f.h) ? f.h : 120) / 100} side={res?.target.side ?? f.form.side}
                 offset={(res?.target.offset ?? f.o / 100) || 0} noScanWhy={why} blocked={f.blocked}
                 onApproach={approach} send={send} />
      <div className="op-people-layout op-laser-layout">
        <div className="feature-card op-people-cam">
          <h3><span>Камера 1 сейчас</span></h3>
          <CameraView cam="0" className="op-people-cam-view" />
          <p className="op-people-cam-note">Красная точка луча видна в кадре; кнопки поворота — поверх кадра.</p>
        </div>
        <div className="op-people-panels">
          <AimCard st={st} send={send} f={f} />
          <MeterCard st={st} send={send} />
          <CalibrationCard st={st} send={send} />
        </div>
      </div>
    </div>
  );
};

export default LaserPanel;
